import type { Property } from '@/types/property';

interface PropertyCardProps {
  property: Property;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

function formatUpdated(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function hasCoords(p: Property): boolean {
  return p.latitude !== 0 || p.longitude !== 0;
}

export function PropertyCard({ property, onSelect, onDelete }: PropertyCardProps) {
  const notesPreview =
    property.notes && property.notes.length > 70
      ? `${property.notes.slice(0, 70)}…`
      : property.notes;

  const coordLabel = hasCoords(property)
    ? `${property.latitude.toFixed(4)}, ${property.longitude.toFixed(4)}`
    : 'No coordinates set';

  // Human-readable zone: "northern_midwest" → "Northern Midwest"
  const zoneLabel = property.zone
    ? property.zone.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '';

  const stateZone = [property.state, zoneLabel].filter(Boolean).join(' · ');

  return (
    <div
      className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-[var(--shadow-card)] p-4 cursor-pointer hover:bg-[var(--color-elevated)] transition-colors duration-100"
      onClick={() => onSelect(property.id)}
      role="button"
      tabIndex={0}
      aria-label={`View ${property.name}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(property.id);
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-display text-display-sm text-[var(--color-text-display)] truncate">
              {property.name}
            </span>
            {/* M-07: Primary badge mirrors SeasonDetailPage pattern (RULE-088: copied inline). */}
            {property.isPrimary && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-accent-subtle)] text-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/30 flex-shrink-0"
                data-testid="primary-badge"
              >
                Primary
              </span>
            )}
          </div>

          {stateZone && (
            <span className="text-body-xs text-[var(--color-accent)] font-medium">
              {stateZone}
            </span>
          )}

          <span className="text-body-xs text-[var(--color-text-muted)]">{coordLabel}</span>

          {notesPreview && (
            <span className="text-body-sm text-[var(--color-text-secondary)] mt-0.5 leading-snug">
              {notesPreview}
            </span>
          )}

          <span className="text-body-xs text-[var(--color-text-muted)] mt-1">
            Updated {formatUpdated(property.updatedAt)}
          </span>
        </div>

        <button
          type="button"
          className="text-body-xs px-2 py-1 flex-shrink-0 bg-transparent text-red-400 hover:bg-red-950/40 border border-red-800/50 hover:border-red-700 rounded-sm font-medium transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(property.id);
          }}
          aria-label={`Delete ${property.name}`}
        >
          Delete
        </button>
      </div>
    </div>
  );
}
